/**
 * variant-media-filter.js
 * -----------------------
 * Hides product media items that don't belong to the currently selected variant.
 *
 * Source of truth: a <script type="application/json" data-variant-media-map="...">
 * tag emitted by snippets/product-media-gallery.liquid. Shape:
 *
 *   {
 *     "section_id": "...",
 *     "ownership": [["mediaId", "variantId"], ...],
 *     "unrestricted_variants": ["variantIdA", ...],
 *     "fallback_variant": "variantId"
 *   }
 *
 * Behaviour:
 *   - Listens to the global `variant:changed` CustomEvent dispatched by
 *     product-info.js (line 184 in PDP revamp theme).
 *   - On change, hides slides + thumbnails whose media id is not assigned
 *     to the selected variant.
 *   - Variants in `unrestricted_variants` show everything (used for variants
 *     without a featured_media).
 *   - On initial load, reads ?variant= from the URL or the form's hidden id
 *     input and applies the same filter so the gallery is correct on landing.
 *   - After every filter pass, calls Swiper#update() if the gallery exposes
 *     one — keeps pagination + nav buttons in sync with visible slides.
 */
(function () {
  'use strict';

  function readMaps() {
    const tags = document.querySelectorAll('script[data-variant-media-map]');
    const maps = [];
    tags.forEach((tag) => {
      try {
        const data = JSON.parse(tag.textContent || '{}');
        const sectionId = data.section_id;
        const ownership = new Map();
        (data.ownership || []).forEach(([mediaId, variantId]) => {
          if (!ownership.has(variantId)) ownership.set(variantId, new Set());
          ownership.get(variantId).add(String(mediaId));
        });
        maps.push({
          sectionId: String(sectionId),
          ownership,
          unrestricted: new Set((data.unrestricted_variants || []).map(String)),
          fallback: String(data.fallback_variant || ''),
          desktopGrid: Boolean(data.desktop_grid_enabled),
        });
      } catch (e) {
        console.warn('[variant-media-filter] bad JSON in', tag, e);
      }
    });
    return maps;
  }

  function findGalleryElement(sectionId) {
    return document.getElementById('MediaGallery-' + sectionId);
  }

  function findFormVariantId() {
    const fromUrl = new URLSearchParams(location.search).get('variant');
    if (fromUrl) return String(fromUrl);
    const input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    return input ? String(input.value) : '';
  }

  function applyForVariant(map, variantId) {
    const gallery = findGalleryElement(map.sectionId);
    if (!gallery) return;

    const slides = gallery.querySelectorAll(
      '.product__media-item[data-media-id], .product__thumbs-item[data-media-id]'
    );
    if (!slides.length) return;

    const showAll =
      !variantId ||
      map.unrestricted.has(variantId) ||
      !map.ownership.has(variantId) ||
      (map.ownership.get(variantId) || new Set()).size === 0;

    const allowed = showAll ? null : map.ownership.get(variantId);

    const styleEl = document.querySelector('style[data-vmm-style="' + map.sectionId + '"]');

    function writeStyle() {
      if (!styleEl) return;
      if (showAll) {
        styleEl.textContent = '';
      } else {
        const sid = map.sectionId.replace(/"/g, '\\"');
        const vid = String(variantId).replace(/"/g, '\\"');
        styleEl.textContent =
          'media-gallery#MediaGallery-' + sid + ' .product__media-item[data-owner-variant]:not([data-owner-variant=""]):not([data-owner-variant="' + vid + '"]),' +
          'media-gallery#MediaGallery-' + sid + ' .product__thumbs-item[data-owner-variant]:not([data-owner-variant=""]):not([data-owner-variant="' + vid + '"]) { display: none !important; }';
      }
    }

    // ---- Preload-then-swap to avoid layout shift on variant change ----
    // Find the FIRST main slide (in document order) that the new variant owns
    // and wait for its image to fully load before updating the filter style.
    // Until then we keep the previous variant's slides visible and overlay a
    // spinner so the gallery never collapses or shows an empty box.
    const mainListForPreload = gallery.querySelector('.product__media-list');
    let preloadImg = null;
    if (mainListForPreload && !showAll && allowed) {
      const candidates = mainListForPreload.querySelectorAll('.product__media-item[data-media-id]');
      for (let i = 0; i < candidates.length; i++) {
        const s = candidates[i];
        if (s.classList.contains('swiper-slide-duplicate')) continue;
        const mid = String(s.getAttribute('data-media-id'));
        if (allowed.has(mid)) {
          preloadImg = s.querySelector('img');
          break;
        }
      }
    }

    // Inline swiper / classes work — runs only after the new variant's first
    // image has decoded so the gallery never collapses during a switch.
    function doSwiperWork() {
      const isDesktop = window.matchMedia('(min-width: 768px)').matches;
      const useSwiperPath = !isDesktop || !map.desktopGrid;

      slides.forEach((slide) => {
        if (slide.classList.contains('swiper-slide-duplicate')) return;
        const mid = String(slide.getAttribute('data-media-id'));
        const visible = showAll || (allowed && allowed.has(mid));
        slide.toggleAttribute('hidden', !visible);
        slide.style.display = visible ? '' : 'none';
        slide.classList.remove('pdp-grid-first');
        if (useSwiperPath) {
          if (visible) {
            slide.classList.add('swiper-slide');
          } else {
            slide.classList.remove('swiper-slide');
          }
        }
      });

      const mainList = gallery.querySelector('.product__media-list');
      if (mainList) {
        const mainSlides = mainList.querySelectorAll('.product__media-item[data-media-id]');
        for (let i = 0; i < mainSlides.length; i++) {
          const s = mainSlides[i];
          if (s.classList.contains('swiper-slide-duplicate')) continue;
          if (s.style.display !== 'none' && !s.hasAttribute('hidden')) {
            s.classList.add('pdp-grid-first');
            break;
          }
        }
      }

      if (!useSwiperPath) return;

      function refreshSwiper(sw) {
        if (!sw || typeof sw.update !== 'function') return;
        try {
          if (sw.params && sw.params.loop && typeof sw.loopDestroy === 'function') sw.loopDestroy();
          sw.update();
          if (sw.params && sw.params.loop && typeof sw.loopCreate === 'function') {
            sw.loopCreate();
            sw.update();
          }
          if (sw.params && sw.params.loop && typeof sw.slideToLoop === 'function') {
            sw.slideToLoop(0, 0, false);
          } else if (typeof sw.slideTo === 'function') {
            sw.slideTo(0, 0, false);
          }
        } catch (e) {
          /* swiper not ready */
        }
      }
      refreshSwiper(gallery.sliderInstance && gallery.sliderInstance.slider);
      refreshSwiper(gallery.thumbsInstance && gallery.thumbsInstance.slider);
    }

    function commit() {
      writeStyle();
      doSwiperWork();
      gallery.classList.remove('pdp-gallery-loading');
    }

    if (preloadImg) {
      const settled = preloadImg.complete && preloadImg.naturalWidth > 0;
      if (settled) {
        commit();
        return;
      }
      gallery.classList.add('pdp-gallery-loading');
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        commit();
      };
      preloadImg.addEventListener('load', finish, { once: true });
      preloadImg.addEventListener('error', finish, { once: true });
      setTimeout(finish, 4000);
      try {
        preloadImg.loading = 'eager';
        preloadImg.fetchPriority = 'high';
      } catch (e) {}
      return;
    }
    commit();
  }

  function applyAll(variantId) {
    const maps = window.__VMM_CACHE__ || (window.__VMM_CACHE__ = readMaps());
    maps.forEach((m) => applyForVariant(m, variantId));
  }

  function swiperReady(map) {
    const gallery = findGalleryElement(map.sectionId);
    if (!gallery) return false;
    const slider = gallery.sliderInstance && gallery.sliderInstance.slider;
    return Boolean(slider && slider.update);
  }

  function applyWhenReady(variantId) {
    const maps = window.__VMM_CACHE__ || (window.__VMM_CACHE__ = readMaps());
    // CSS-grid path doesn't need swiper — apply immediately. Swiper path
    // waits for the swiper instance to mount so we don't fight its init.
    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    const allCssGrid = maps.every((m) => isDesktop && m.desktopGrid);
    if (allCssGrid) {
      maps.forEach((m) => applyForVariant(m, variantId));
      return;
    }
    let attempts = 0;
    const tick = () => {
      attempts += 1;
      const allReady = maps.every(swiperReady);
      if (allReady || attempts > 30) {
        maps.forEach((m) => applyForVariant(m, variantId));
        return;
      }
      setTimeout(tick, 100);
    };
    tick();
  }

  function showLoaderImmediately() {
    // Light up the spinner the instant the user touches a swatch — gives
    // immediate visual feedback even when the new image is already cached.
    document.querySelectorAll('media-gallery').forEach((g) => {
      g.classList.add('pdp-gallery-loading');
    });
  }

  function reapplyAfterVariantChange() {
    showLoaderImmediately();
    // Form input is updated asynchronously by product-info.js after the
    // section-rendering fetch resolves. Poll briefly until it changes,
    // then apply the filter. Fall back to the URL ?variant= param if the
    // form input never moves (e.g. some themes only update the URL).
    let last = findFormVariantId();
    let attempts = 0;
    const startVariant = last;
    const tick = () => {
      const cur = findFormVariantId();
      if (cur && cur !== startVariant) {
        applyAll(cur);
        return;
      }
      attempts += 1;
      if (attempts > 40) {
        // 40 * 50ms = 2s budget. Apply with whatever the URL says now.
        applyAll(findFormVariantId());
        return;
      }
      setTimeout(tick, 50);
    };
    tick();
  }

  function init() {
    // Wait for swipers, then filter for the initial variant.
    applyWhenReady(findFormVariantId());

    // Standard variant:changed dispatcher (Dawn / product-info pattern).
    document.addEventListener('variant:changed', (event) => {
      const variant = event.detail && event.detail.variant;
      if (!variant) return;
      applyAll(String(variant.id));
    });

    // Hometown's product-info doesn't always reach the variant:changed
    // dispatch, so subscribe to the FoxTheme pubsub events that DO fire.
    if (window.FoxTheme && window.FoxTheme.pubsub) {
      const ps = window.FoxTheme.pubsub;
      const evs = ps.PUB_SUB_EVENTS || {};
      if (evs.optionValueSelectionChange) {
        ps.subscribe(evs.optionValueSelectionChange, reapplyAfterVariantChange);
      }
      if (evs.variantChange) {
        ps.subscribe(evs.variantChange, (payload) => {
          const v = payload && payload.data && payload.data.variant;
          if (v && v.id) applyAll(String(v.id));
        });
      }
    }

    // Direct change event on variant-selects + small delay so the URL /
    // form input has time to update.
    document.querySelectorAll('variant-selects').forEach((el) => {
      el.addEventListener('change', () => {
        setTimeout(reapplyAfterVariantChange, 0);
      });
    });

    // Re-apply on full window load — swiper sometimes initialises late.
    window.addEventListener('load', () => applyWhenReady(findFormVariantId()));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
